import { routing } from '../../i18n/routing';

// The URL segment for the personal (no-org) workspace, e.g. `/personal/pull-requests`.
export const PERSONAL_SLUG = 'personal';

// Slugs that can never address an org. Locale codes are claimed by the `[locale]`
// segment (`/de` is the German home, never an org named `de`), the legacy roots
// are gated by `src/proxy.ts`, and the cross-org page segments (`/work-items`,
// `/reviews`, etc.) are claimed by the `(cross-org)` route group. An org whose
// GitHub login collides with one of these is unreachable on the new surface —
// documented, not silently broken. `org-slug.test.ts` derives the route
// segments from the app tree, so a new top-level route fails until it is
// reserved here.
const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  ...routing.locales.map((locale) => locale.toLowerCase()),
  'activity',
  'assistant',
  'contributor',
  'customers',
  'delivery',
  'examples',
  'health',
  'inbox',
  'legacy',
  'org',
  'profile',
  'reports',
  'reviews',
  'revenue',
  'settings',
  'train',
  'work-items',
]);

/**
 * Canonical form of a raw URL segment: percent-decoded, trimmed, lower-cased. The
 * GitHub login stored in org metadata is matched case-insensitively, so this is
 * the single normalisation used everywhere a slug is read or compared.
 */
export function canonicaliseOrgSlug(raw: string): string {
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    // Malformed percent-encoding: fall back to the raw value (it will fail
    // `isValidOrgSlug` below).
    decoded = raw;
  }
  return decoded.trim().toLowerCase();
}

/** True when the slug is reserved (a locale code or a legacy route root). */
export function isReservedOrgSlug(slug: string): boolean {
  return RESERVED_SLUGS.has(slug);
}

/**
 * True when `slug` is a usable org slug: GitHub-login-shaped (`a-z0-9-`) and not
 * reserved. `PERSONAL_SLUG` is valid. Callers should pass an already-canonical
 * slug; this also tolerates a raw one defensively.
 */
export function isValidOrgSlug(slug: string): boolean {
  const canonical = canonicaliseOrgSlug(slug);
  if (!/^[\da-z-]+$/.test(canonical)) return false;
  return !isReservedOrgSlug(canonical);
}
